
import Vue from "vue";
import { v4 as uuidv4 } from "uuid";

export default Vue.extend({
  props: {
    showPlaceholder: Boolean,
    relationshipAtoB: Object,
    relationshipBtoA: Object,
    distance: Number, // 左上を基点としたAとBの距離
    degree: Number, // 水平を基準とした角度
    fontSize: Number,
  },
  data() {
    return {
      relAtoB: {
        id: uuidv4(),
        relationship: this.relationshipAtoB.relationship,
        color: this.relationshipAtoB.color,
      },
      relBtoA: {
        id: uuidv4(),
        relationship: this.relationshipBtoA.relationship,
        color: this.relationshipBtoA.color,
      },
      lineLength: this.distance ? this.distance - 215 : 85,
      fontSizeVal: this.fontSize || 12,
    };
  },
  methods: {
    /** markerを指定するための一意なidを付与する */
    addId(object) {
      return {
        id: uuidv4(),
        relationship: object.relationship,
        color: this.transformationColorCode(object.color),
      };
    },
    ellipsisText(text) {
      if (text) {
        // 文字列の横幅を計算するためのspanを作成
        const span = document.createElement("span");
        span.style.position = "absolute";
        span.style.top = "-1000px";
        span.style.left = "-1000px";
        span.style.whiteSpace = "nowrap";
        span.style.fontSize = `${this.fontSizeVal}px`;
        span.innerHTML = text;
        document.body.appendChild(span);
        const width = span.clientWidth;

        // svgの幅よりも大きい場合は文字を省略
        if (width > this.svgWidth) {
          const averageCharLength = width / text.length;
          const availableCharLength = Math.floor(this.svgWidth / averageCharLength);
          // eslint-disable-next-line no-param-reassign
          text = `${text.slice(0, availableCharLength - 2)}…`;
        }

        // spanを削除
        document.body.removeChild(span);
      }
      return text;
    },
  },
  computed: {
    textColor() {
      // CSS変数からテキスト色を取得
      if (typeof window !== "undefined") {
        const computedStyle = getComputedStyle(document.documentElement);
        return computedStyle.getPropertyValue("--color-text").trim() || "#383838";
      }
      return "#383838";
    },
    svgWidth() {
      const { lineLength } = this;
      return lineLength + 15;
    },
    svgHeight() {
      const { degree } = this;

      // tan(90)は存在しないのでsvgHeight = svgWidthとする
      if (Math.abs(degree) === 90) {
        return this.svgWidth;
      }

      let height = 0;
      if (degree) {
        // 直角三角形の底辺(a)と角度(θ)から高さを求める = a * tan(θ)
        const radian = (degree * Math.PI) / 180;
        height = Math.abs(this.svgWidth * Math.tan(radian));
      }
      return height + 70 + (this.fontSizeVal - 12) * 2;
    },
    /** アプリのカラーコード(#AARRGGBB)をWebのカラーコード(#RRGGBB)に変換 */
    transformationColorCode() {
      return (colorCode: string) => {
        if (colorCode.length === 9) {
          return `#${colorCode.slice(3)}`;
        }
        return colorCode;
      };
    },
    /** 矢印の色を取得（ダークモード対応） */
    getArrowColor() {
      return (colorCode: string) => {
        const color = this.transformationColorCode(colorCode);
        // ダークモードかどうかを判定
        if (typeof window !== "undefined") {
          const computedStyle = getComputedStyle(document.documentElement);
          const bgColor = computedStyle.getPropertyValue("--color-bg").trim();
          // 背景色が暗い場合（ダークモード）は、暗い色を明るくする
          if (this.isDarkMode(bgColor)) {
            return this.adjustColorForDarkMode(color);
          }
        }
        return color;
      };
    },
    /** ストローク幅を取得（暗い色の場合は太くする） */
    getStrokeWidth() {
      return (colorCode: string) => {
        const color = this.transformationColorCode(colorCode);
        // ダークモードで暗い色の場合は太くする
        if (typeof window !== "undefined") {
          const computedStyle = getComputedStyle(document.documentElement);
          const bgColor = computedStyle.getPropertyValue("--color-bg").trim();
          if (this.isDarkMode(bgColor) && this.isDarkColor(color)) {
            return 2.5;
          }
        }
        return 1.5;
      };
    },
    /** ストロークの不透明度を取得（暗い色の場合は不透明度を上げる） */
    getStrokeOpacity() {
      return (colorCode: string) => {
        const color = this.transformationColorCode(colorCode);
        // ダークモードで暗い色の場合は不透明度を上げる
        if (typeof window !== "undefined") {
          const computedStyle = getComputedStyle(document.documentElement);
          const bgColor = computedStyle.getPropertyValue("--color-bg").trim();
          if (this.isDarkMode(bgColor) && this.isDarkColor(color)) {
            return 1;
          }
        }
        return 1;
      };
    },
    /** ダークモードかどうかを判定 */
    isDarkMode() {
      return (bgColor: string) => {
        // 背景色が暗い色（例: #1a1a1a, #2d2d2dなど）の場合はダークモードと判定
        const rgb = this.hexToRgb(bgColor);
        if (!rgb) return false;
        // 明度が低い（128以下）場合はダークモード
        const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
        return brightness < 128;
      };
    },
    /** 色が暗いかどうかを判定 */
    isDarkColor() {
      return (color: string) => {
        const rgb = this.hexToRgb(color);
        if (!rgb) return false;
        // 明度が低い（128以下）場合は暗い色
        const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
        return brightness < 128;
      };
    },
    /** ダークモード用に色を調整（暗い色を明るくする） */
    adjustColorForDarkMode() {
      return (color: string) => {
        const rgb = this.hexToRgb(color);
        if (!rgb) return color;

        // 明度が低い色（黒、グレーなど）を明るくする
        // 明度計算: (r * 299 + g * 587 + b * 114) / 1000
        const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
        if (brightness < 100) {
          // 非常に暗い色（黒 #000000、濃い青 #000080など）は明るいグレーに変更
          // これにより、ダークモードでも視認性を確保
          return "#CCCCCC";
        } if (brightness < 128) {
          // 暗い色（グレー #909090、濃い色など）は1.5倍明るくする
          // 例: #2B2B2B (brightness: 43) → #414141 (brightness: 65)
          const factor = 1.5;
          const newR = Math.min(255, Math.round(rgb.r * factor));
          const newG = Math.min(255, Math.round(rgb.g * factor));
          const newB = Math.min(255, Math.round(rgb.b * factor));
          return this.rgbToHex(newR, newG, newB);
        }
        // 明るい色（brightness >= 128）はそのまま使用
        return color;
      };
    },
    /** 16進数カラーコードをRGBに変換 */
    hexToRgb() {
      return (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
          ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
            }
          : null;
      };
    },
    /** RGBを16進数カラーコードに変換 */
    rgbToHex() {
      return (r: number, g: number, b: number) => `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
    },
  },
  watch: {
    relationshipAtoB: {
      handler() {
        this.relAtoB = this.addId(this.relationshipAtoB);
      },
      deep: true,
      immediate: true,
    },
    relationshipBtoA: {
      handler() {
        this.relBtoA = this.addId(this.relationshipBtoA);
      },
      deep: true,
      immediate: true,
    },
    distance() {
      this.lineLength = this.distance ? this.distance - 215 : 85;
    },
  },
});
